import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { CrosshairIcon, FilmIcon, Trash2Icon, UploadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Field } from './Field';
import { ImageField } from './ImageField';
import { ProductImage } from '../../ui/ProductImage';
import { inputCls } from './styles';
import { MEDIA_ALT_MAX, clampFocus, focusPosition, mediaProblem } from '../../core/rules/media';
import type { Media } from '../../core/types/media';
type Props = {
  label: string;
  hint?: string;
  value: Media;
  onChange: (value: Media) => void;
  /** Of een video hier past. Productgalerij en sfeerbeelden: ja. */
  allowVideo?: boolean;
  /** Wat er staat als het leeg blijft, in woorden. */
  emptyNote?: string;
};

/** Hoogstens zoveel MB video in de demo; de server neemt tot 150 MB aan (LARAVEL.md §29). */
const MAX_VIDEO_MB = 150;
/** Zoveel procent per pijltjestoets bij het belangrijkste punt. */
const FOCUS_STEP = 5;
const ARROWS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1]
};

/**
 * Eén beeld voor de site: foto (slepen, kiezen of link), beschrijving, het belangrijkste punt
 * en optioneel een korte video. Rechts zie je meteen hoe het op telefoon (staand) en
 * computer (liggend) wordt uitgesneden.
 *
 * Video in de demo is een tijdelijke link (alleen deze sessie). Met de server: uploaden naar
 * `POST /admin/media`, die een 1080p- en 720p-versie en een poster maakt.
 */
export function MediaField({
  label,
  hint,
  value,
  onChange,
  allowVideo = true,
  emptyNote = 'Leeg: de standaardfoto van deze plek.'
}: Props) {
  const videoRef = useRef<HTMLInputElement>(null);
  const altId = useId();
  const set = (patch: Partial<Media>) => onChange({
    ...value,
    ...patch
  });
  // Een gekozen bestand is alleen een voorbeeld in dit tabblad (blob-link); het concept krijgt pas
  // een video als de server een vaste link teruggeeft (POST /admin/media, LARAVEL.md §29).
  const [localVideo, setLocalVideo] = useState<string | null>(null);
  useEffect(() => () => {
    if (localVideo) URL.revokeObjectURL(localVideo);
  }, [localVideo]);
  const pendingVideo = localVideo !== null && !value.video;
  const problem = mediaProblem(value, {
    pendingVideo
  });
  const position = focusPosition(value.focus) ?? '50% 50%';
  const focus = value.focus ?? {
    x: 50,
    y: 50
  };
  const pickFocus = (e: MouseEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    set({
      focus: clampFocus({
        x: (e.clientX - r.left) / r.width * 100,
        y: (e.clientY - r.top) / r.height * 100
      })
    });
  };
  const nudgeFocus = (e: KeyboardEvent<HTMLButtonElement>) => {
    const d = ARROWS[e.key];
    if (!d) return;
    e.preventDefault();
    set({
      focus: clampFocus({
        x: focus.x + d[0] * FOCUS_STEP,
        y: focus.y + d[1] * FOCUS_STEP
      })
    });
  };
  const takeVideo = (file?: File) => {
    if (!file) return;
    if (!/^video\/(mp4|quicktime)$/.test(file.type) || file.size > MAX_VIDEO_MB * 1024 * 1024) {
      toast.error('Deze video kan niet worden gebruikt', {
        description: `Kies een mp4 of mov van maximaal ${MAX_VIDEO_MB} MB en hoogstens een minuut.`
      });
      return;
    }
    setLocalVideo(URL.createObjectURL(file));
    toast('Voorbeeld van je video', {
      description: 'Hij wordt nog niet bewaard: uploaden kan pas met de server. Plak zolang een link naar een mp4.'
    });
  };
  return <Field label={label} hint={hint} as="div" data-id="element-5282" data-mp="gmhosiu02xdw">
      <div className="grid gap-5 md:grid-cols-[minmax(0,14rem)_1fr]" data-id="element-5283" data-mp="glg3zmn6mwiz">
        <div className="space-y-3" data-id="element-5284" data-mp="kpnrjghwm6fm">
          <ImageField value={value.image} onChange={image => set({
          image
        })} fit="cover" aspect="aspect-[4/3]" data-id="element-5285" data-mp="kpxgr1v4k9k9" />
          {!value.image && <p className="text-[12px] text-muted" data-id="element-5286" data-mp="bno8yjr8quak">{emptyNote}</p>}
        </div>

        <div className="min-w-0 space-y-4" data-id="element-5287" data-mp="bpra9zxug9ul">
          <label className="block" htmlFor={altId} data-id="element-5288" data-mp="212kzshblrplw">
            <span className="flex items-baseline justify-between gap-3" data-id="element-5289" data-mp="2111hymaat6i0">
              <span className="text-[13px] font-medium text-ink" data-id="element-5290" data-mp="261fi7i5nrc5h">Wat is er te zien?</span>
              <span className={`text-[12px] tabular-nums ${value.alt.length > MEDIA_ALT_MAX ? 'text-clay' : 'text-muted'}`} data-id="element-5291" data-mp="5yenjeqnywb1">
                {value.alt.length} / {MEDIA_ALT_MAX}
              </span>
            </span>
            <span className="mt-0.5 block text-[12px] text-muted" data-id="element-5292" data-mp="3t2kpvxqdca8v">Voor blinde bezoekers en Google. Leeg = alleen sfeer.</span>
            <input id={altId} value={value.alt} onChange={e => set({
            alt: e.target.value
          })} className={`${inputCls} mt-1.5`} data-id="element-5293" data-mp="3j67v0wj37qcj" />
          </label>

          {value.image && <div data-id="element-5294" data-mp="3edergbzcuo21">
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-5295" data-mp="39gek7xqrrf9c">
                <CrosshairIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5296" data-mp="39e24lr8hhgzg" />
                Belangrijkste punt
              </p>
              <p className="mt-0.5 text-[12px] text-muted" data-id="element-5297" data-mp="30ef2f37xk75c">Klik op wat altijd in beeld moet blijven (of gebruik de pijltjes).</p>
              <div className="mt-2 flex flex-wrap items-start gap-3" data-id="element-5298" data-mp="2viqbtpdw20l6">
                <button type="button" onClick={pickFocus} onKeyDown={nudgeFocus} aria-label={`Belangrijkste punt: ${focus.x}% van links, ${focus.y}% van boven. Klik of gebruik de pijltjes om te verplaatsen.`} className="relative h-28 w-28 shrink-0 cursor-crosshair overflow-hidden rounded-md bg-sand" data-id="element-5299" data-mp="1axt9ezw546lf">
                  <ProductImage src={value.image} fit="contain" className="h-full w-full" data-id="element-5300" data-mp="s9obi925zfuj" />
                  <span aria-hidden="true" className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-shell bg-clay shadow-knob" style={{
                left: `${focus.x}%`,
                top: `${focus.y}%`
              }} data-id="element-5301" data-mp="x7zmin473wec" />
                </button>
                {/* Zo wordt het uitgesneden op de site. */}
                <figure className="w-[4.5rem]" data-id="element-5302" data-mp="difdpco3yo6b">
                  <div className="aspect-[4/5] overflow-hidden rounded-sm bg-sand" data-id="element-5303" data-mp="dh49jbqr3eeo">
                    <ProductImage src={value.image} fit="cover" position={position} className="h-full w-full" data-id="element-5304" data-mp="1gsa1zezpz7rf" />
                  </div>
                  <figcaption className="mt-1 text-[11px] text-muted" data-id="element-5305" data-mp="1qjxwjorkp4x4">Telefoon</figcaption>
                </figure>
                <figure className="w-40" data-id="element-5306" data-mp="1lkbaqqxoigcp">
                  <div className="aspect-[16/9] overflow-hidden rounded-sm bg-sand" data-id="element-5307" data-mp="1llcxhso24ra9">
                    <ProductImage src={value.image} fit="cover" position={position} className="h-full w-full" data-id="element-5308" data-mp="xw1ue4j8iksy" />
                  </div>
                  <figcaption className="mt-1 text-[11px] text-muted" data-id="element-5309" data-mp="syifr3imk60h">Computer</figcaption>
                </figure>
              </div>
            </div>}

          {allowVideo && <div className="border-t border-line pt-4" data-id="element-5310" data-mp="12wg2fmku5uqz">
              <p className="flex items-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-5311" data-mp="2ia2fdizopjw8">
                <FilmIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5312" data-mp="2eaa4gzmtj1np" />
                Korte video (optioneel)
              </p>
              <p className="mt-0.5 text-[12px] text-muted" data-id="element-5313" data-mp="2lvdzyaic0yif">
                Een stille loop van 6 tot 15 seconden. Speelt niet voor wie minder beweging wil; dan staat de foto er.
              </p>
              {value.video || pendingVideo ? <div className="mt-2 flex flex-wrap items-center gap-3" data-id="element-5314" data-mp="2gt567bxmxsf9">
                  <video src={value.video?.src ?? localVideo ?? undefined} muted loop playsInline autoPlay className="h-20 w-32 rounded-sm bg-sand object-cover" aria-hidden="true" data-id="element-5315" data-mp="34jsqkwoqmaip" />
                  <button type="button" onClick={() => {
              setLocalVideo(null);
              if (value.video) onChange({
                image: value.image,
                alt: value.alt,
                ...(value.focus ? {
                  focus: value.focus
                } : {})
              });
            }} className="inline-flex items-center gap-1.5 rounded-md px-2 py-2 text-[13px] text-clay transition-colors duration-150 ease-soft hover:bg-sand" data-id="element-5316" data-mp="39kgdp79ui77n">
                    <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5317" data-mp="1s85e46e6k9vi" />
                    Video weghalen
                  </button>
                </div> : <div className="mt-2 flex min-w-0 gap-2" data-id="element-5318" data-mp="1jesb2bmejrvp">
                  <input placeholder="Plak een link naar een mp4" aria-label="Link naar een video" className={inputCls} onBlur={e => {
              const src = e.target.value.trim();
              if (src) set({
                video: {
                  src
                }
              });
            }} data-id="element-5319" data-mp="1eg8ms4kc6406" />
                  <button type="button" onClick={() => videoRef.current?.click()} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-line bg-white px-3 text-[13px] text-ink transition-colors duration-150 ease-soft hover:border-ink/40" data-id="element-5320" data-mp="1x5xd1kd8sw97">
                    <UploadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5321" data-mp="bnwh8qfpfv93" />
                    Kies
                    <span className="sr-only" data-id="element-5322" data-mp="gmfyidjcsiv4"> een video van je apparaat</span>
                  </button>
                  <input ref={videoRef} type="file" accept="video/mp4,video/quicktime" className="hidden" tabIndex={-1} aria-hidden="true" onChange={e => takeVideo(e.target.files?.[0])} data-id="element-5323" data-mp="ljpy2m0giawo" />
                </div>}
            </div>}

          {problem && <p role="alert" className="text-[13px] text-clay" data-id="element-5324" data-mp="19abwdlknsgth">
              {problem}
            </p>}
        </div>
      </div>
    </Field>;
}